/**
 * Embed (`select=jobs(job_number)`) resolution + column projection.
 *
 * There is no schema at runtime — `src/types/database.ts` is types-only, so the
 * generated `Relationships` are erased. Relationships are therefore inferred
 * from column NAMES and then confirmed against the DATA: every candidate
 * foreign key is scored by how many parent rows it actually resolves, and the
 * best-scoring pair wins. That handles the awkward ones this app uses, e.g.
 * `job_participants.staff_user_id → profiles.user_id` (profiles has no `id`)
 * and `compliance_tasks.workflow_id → compliance_workflows.id`.
 */
import type { ParsedSelect, SelectField } from './select';
import { fieldsToParsed } from './select';
import type { DemoStore } from './store';
import type { Row } from './types';
import { isNullish } from './values';

export type Relation =
  | { kind: 'to-one'; parentCol: string; childKey: string }
  | { kind: 'to-many'; parentKey: string; childCol: string }
  | { kind: 'none' };

const SAMPLE = 200;

function unionKeys(rows: Row[], limit = 50): Set<string> {
  const keys = new Set<string>();
  for (let i = 0; i < rows.length && i < limit; i += 1) {
    for (const k of Object.keys(rows[i]!)) keys.add(k);
  }
  return keys;
}

function singularize(name: string): string {
  if (name.endsWith('ies')) return `${name.slice(0, -3)}y`;
  if (/(sses|ches|shes|xes|zes)$/.test(name)) return name.slice(0, -2);
  if (name.endsWith('s') && !name.endsWith('ss')) return name.slice(0, -1);
  return name;
}

/**
 * Column names a parent might use to point at `table`, most specific first:
 * `compliance_workflows` → `compliance_workflow_id`, `workflow_id`.
 */
function fkNameCandidates(table: string): string[] {
  const base = singularize(table.replace(/^v_/, ''));
  const parts = base.split('_');
  const out: string[] = [];
  for (let i = 0; i < parts.length; i += 1) out.push(`${parts.slice(i).join('_')}_id`);
  out.push(`${table}_id`);
  return Array.from(new Set(out));
}

function indexBy(rows: Row[], key: string): Map<string, Row[]> {
  const m = new Map<string, Row[]>();
  for (const r of rows) {
    const v = r[key];
    if (isNullish(v)) continue;
    const k = String(v);
    const bucket = m.get(k);
    if (bucket) bucket.push(r);
    else m.set(k, [r]);
  }
  return m;
}

function matchRate(parentRows: Row[], parentCol: string, index: Map<string, Row[]>): number {
  let seen = 0;
  let hit = 0;
  for (let i = 0; i < parentRows.length && seen < SAMPLE; i += 1) {
    const v = parentRows[i]![parentCol];
    if (isNullish(v)) continue;
    seen += 1;
    if (index.has(String(v))) hit += 1;
  }
  return seen === 0 ? 0 : hit / seen;
}

type Candidate = { parentCol: string; childKey: string; score: number };

function toOneCandidates(parentCols: Set<string>, childCols: Set<string>, childTable: string): Candidate[] {
  const out: Candidate[] = [];
  const seen = new Set<string>();
  const push = (parentCol: string, childKey: string, score: number) => {
    const k = `${parentCol}>${childKey}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ parentCol, childKey, score });
  };

  const childKeys = ['id', 'user_id', ...fkNameCandidates(childTable)]
    .filter((k) => childCols.has(k));
  if (childKeys.length === 0 && childCols.has('id')) childKeys.push('id');

  const fkNames = fkNameCandidates(childTable);
  for (const childKey of childKeys) {
    fkNames.forEach((fk, i) => {
      if (parentCols.has(fk)) push(fk, childKey, 1000 - i);
    });
    if (childKey === 'user_id') {
      if (parentCols.has('user_id')) push('user_id', childKey, 900);
      for (const pc of parentCols) if (pc.endsWith('_user_id')) push(pc, childKey, 800);
    }
    // Data-verified fallback: any *_id / *_by column could be the FK.
    for (const pc of parentCols) {
      if (pc === 'id') continue;
      if (pc.endsWith('_id') || pc.endsWith('_by')) push(pc, childKey, 100);
    }
  }
  return out;
}

/** Infers how `childTable` hangs off `parentTable`, memoised on the store. */
export function inferRelation(
  store: DemoStore,
  parentTable: string,
  parentRows: Row[],
  childTable: string,
  childRows: Row[],
): Relation {
  const cacheKey = `${parentTable}>${childTable}`;
  const cached = store.relationCache.get(cacheKey) as Relation | undefined;
  if (cached) return cached;

  const parentCols = unionKeys(parentRows);
  const childCols = unionKeys(childRows);

  let best: (Candidate & { rate: number }) | null = null;
  const indexes = new Map<string, Map<string, Row[]>>();
  for (const c of toOneCandidates(parentCols, childCols, childTable)) {
    let idx = indexes.get(c.childKey);
    if (!idx) { idx = indexBy(childRows, c.childKey); indexes.set(c.childKey, idx); }
    const rate = matchRate(parentRows, c.parentCol, idx);
    if (!best
      || rate > best.rate
      || (rate === best.rate && c.score > best.score)) {
      best = { ...c, rate };
    }
  }

  let relation: Relation = { kind: 'none' };
  if (best && best.rate > 0) {
    relation = { kind: 'to-one', parentCol: best.parentCol, childKey: best.childKey };
  } else {
    // No to-one hit — try the reverse (parent.id ← child.<parent>_id).
    const parentKey = ['id', 'user_id'].find((k) => parentCols.has(k));
    const childCol = parentKey
      ? fkNameCandidates(parentTable).find((fk) => childCols.has(fk))
      : undefined;
    if (parentKey && childCol) {
      const idx = indexBy(childRows, childCol);
      if (matchRate(parentRows, parentKey, idx) > 0) {
        relation = { kind: 'to-many', parentKey, childCol };
      }
    }
    if (relation.kind === 'none' && best) {
      relation = { kind: 'to-one', parentCol: best.parentCol, childKey: best.childKey };
    }
  }

  // Only memoise a decision made against real rows; an empty page would
  // otherwise freeze a wrong answer for the rest of the session.
  if (parentRows.length > 0 && childRows.length > 0) store.relationCache.set(cacheKey, relation);
  return relation;
}

function pick(row: Row, cols: string[]): Row {
  const out: Row = {};
  for (const c of cols) out[c] = c in row ? row[c] : null;
  return out;
}

/**
 * Projects `rows` to the requested columns and attaches embeds.
 * `keep[i] === false` means an `!inner` embed found no match, so PostgREST
 * would have dropped that parent row.
 */
export async function projectRows(
  store: DemoStore,
  table: string,
  rows: Row[],
  sel: ParsedSelect,
): Promise<{ out: Row[]; keep: boolean[] }> {
  const takeAll = sel.star || (sel.cols.length === 0 && sel.embeds.length === 0);
  const out: Row[] = rows.map((r) => (takeAll ? { ...r } : pick(r, sel.cols)));
  const keep: boolean[] = rows.map(() => true);
  if (rows.length === 0) {
    // Still resolve nothing — but an inner embed on an empty set is empty anyway.
    return { out, keep };
  }

  for (const embed of sel.embeds) {
    const childRows = await store.table(embed.table);
    const rel = inferRelation(store, table, rows, embed.table, childRows);

    if (rel.kind === 'none') {
      for (let i = 0; i < rows.length; i += 1) {
        out[i]![embed.alias] = null;
        if (embed.inner) keep[i] = false;
      }
      continue;
    }

    const key = rel.kind === 'to-one' ? rel.childKey : rel.childCol;
    const index = indexBy(childRows, key);

    // Flatten every matched child row so the recursive projection runs once.
    const flat: Row[] = [];
    const slots: Array<{ start: number; len: number }> = [];
    for (const parent of rows) {
      const lookup = rel.kind === 'to-one' ? parent[rel.parentCol] : parent[rel.parentKey];
      const bucket = isNullish(lookup) ? undefined : index.get(String(lookup));
      const matched = rel.kind === 'to-one'
        ? (bucket && bucket.length > 0 ? [bucket[0]!] : [])
        : (bucket ?? []);
      slots.push({ start: flat.length, len: matched.length });
      flat.push(...matched);
    }

    const childSel = fieldsToParsed(embed.fields as SelectField[]);
    const child = await projectRows(store, embed.table, flat, childSel);

    for (let i = 0; i < rows.length; i += 1) {
      const { start, len } = slots[i]!;
      const kept: Row[] = [];
      for (let k = 0; k < len; k += 1) if (child.keep[start + k]) kept.push(child.out[start + k]!);
      if (rel.kind === 'to-one') {
        const value = kept[0] ?? null;
        out[i]![embed.alias] = value;
        if (embed.inner && value === null) keep[i] = false;
      } else {
        out[i]![embed.alias] = kept;
        if (embed.inner && kept.length === 0) keep[i] = false;
      }
    }
  }

  return { out, keep };
}
